/**
 * TrackingUrlRulesPanel — first-touch attribution URL rules management.
 * Match URL patterns → assign source tags for attribution.
 * e.g. "utm_source=google" → tag @google_organic
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Loader2,
  Plus,
  Trash2,
  Power,
  Link2,
  X,
  ChevronDown,
  ChevronUp,
  Search,
  Tag,
  Settings2,
} from "lucide-react";

interface UrlRule {
  id: string;
  userId: string;
  name: string;
  ruleType: string;
  tag: string;
  wordsToMatch: string[];
  wordsToIgnore: string[];
  applyTo: string;
  baseDomainOnly: boolean;
  disregardSource: boolean;
  unqualifiedKeywords: string[];
  dynamicSourceParam: string | null;
  dynamicCategoryParam: string | null;
  enabled: boolean;
  priority: number;
  createdAt: string;
  updatedAt: string;
}

const TAG_PREFIXES: Record<string, string> = {
  $: "bg-yellow-500/20 text-yellow-300 border-yellow-500/30",
  "@": "bg-emerald-500/20 text-emerald-300 border-emerald-500/30",
};

function RuleTagBadge({ tag }: { tag: string }) {
  const prefix = tag.charAt(0);
  const colorClass = TAG_PREFIXES[prefix] || "bg-blue-500/20 text-blue-300 border-blue-500/30";
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-mono border ${colorClass}`}
    >
      {tag}
    </span>
  );
}

const APPLY_TO_LABEL_KEYS: Record<string, string> = {
  tracked_url: "urlRules.applyTo.tracked_url",
  previous_url: "urlRules.applyTo.previous_url",
  both: "urlRules.applyTo.both",
};

// ─── Tag Input Component ───────────────────

function TagInput({
  values,
  onChange,
  placeholder,
}: {
  values: string[];
  onChange: (v: string[]) => void;
  placeholder: string;
}) {
  const [input, setInput] = useState("");

  const handleAdd = () => {
    const trimmed = input.trim();
    if (!trimmed || values.includes(trimmed)) return;
    onChange([...values, trimmed]);
    setInput("");
  };

  const handleRemove = (val: string) => {
    onChange(values.filter((v) => v !== val));
  };

  return (
    <div className="space-y-1.5">
      <div className="flex gap-1.5">
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              handleAdd();
            }
          }}
          placeholder={placeholder}
          className="flex-1 h-8 px-2 text-xs bg-[#030407] border border-white/[0.1] rounded-md text-white placeholder:text-white/20"
        />
      </div>
      {values.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {values.map((val) => (
            <span
              key={val}
              className="inline-flex items-center gap-1 px-2 py-0.5 bg-white/[0.08] border border-white/[0.12] rounded text-xs text-white/80"
            >
              {val}
              <button
                onClick={() => handleRemove(val)}
                className="text-white/40 hover:text-red-400"
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Create URL Rule Form ──────────────────

function CreateUrlRuleForm({
  onCreated,
  onCancel,
}: {
  onCreated: () => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [tag, setTag] = useState("");
  const [ruleType, setRuleType] = useState("simple");
  const [wordsToMatch, setWordsToMatch] = useState<string[]>([]);
  const [wordsToIgnore, setWordsToIgnore] = useState<string[]>([]);
  const [applyTo, setApplyTo] = useState("tracked_url");
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [baseDomainOnly, setBaseDomainOnly] = useState(false);
  const [disregardSource, setDisregardSource] = useState(false);
  const [unqualifiedKeywords, setUnqualifiedKeywords] = useState<string[]>([]);
  const [dynamicSourceParam, setDynamicSourceParam] = useState("");

  const createMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      apiRequest("POST", "/api/tracking/url-rules", data),
    onSuccess: onCreated,
  });

  return (
    <div className="p-4 bg-white/[0.03] border border-white/[0.1] rounded-lg space-y-3">
      <h4 className="text-xs font-medium text-white/80 flex items-center gap-1.5">
        <Plus className="w-3.5 h-3.5 text-red-400" /> {t("urlRules.create.title")}
      </h4>

      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="text-xs text-white/50 mb-1 block">{t("urlRules.create.name")}</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t("urlRules.create.namePlaceholder")}
            className="w-full h-8 px-2 text-xs bg-[#030407] border border-white/[0.1] rounded-md text-white placeholder:text-white/20"
          />
        </div>
        <div>
          <label className="text-xs text-white/50 mb-1 block">{t("urlRules.create.tag")}</label>
          <input
            type="text"
            value={tag}
            onChange={(e) => setTag(e.target.value)}
            placeholder={t("urlRules.create.tagPlaceholder")}
            className="w-full h-8 px-2 text-xs bg-[#030407] border border-white/[0.1] rounded-md text-white font-mono placeholder:text-white/20"
          />
        </div>
      </div>

      <div>
        <label className="text-xs text-white/50 mb-1 block">{t("urlRules.create.ruleType")}</label>
        <div className="flex gap-2">
          <button
            onClick={() => setRuleType("simple")}
            className={`px-3 py-1.5 text-xs rounded border transition-colors ${ruleType === "simple" ? "bg-red-600/20 border-red-500/40 text-red-300" : "bg-white/[0.04] border-white/[0.08] text-white/60"}`}
          >
            SIMPLE
          </button>
          <button
            onClick={() => setRuleType("regex")}
            className={`px-3 py-1.5 text-xs rounded border transition-colors ${ruleType === "regex" ? "bg-red-600/20 border-red-500/40 text-red-300" : "bg-white/[0.04] border-white/[0.08] text-white/60"}`}
          >
            REGEX
          </button>
        </div>
      </div>

      <div>
        <label className="text-xs text-white/50 mb-1 block">
          {t("urlRules.create.wordsToMatch")}
        </label>
        <TagInput
          values={wordsToMatch}
          onChange={setWordsToMatch}
          placeholder={t("urlRules.create.wordsToMatchPlaceholder")}
        />
      </div>

      <div>
        <label className="text-xs text-white/50 mb-1 block">
          {t("urlRules.create.wordsToIgnore")}
        </label>
        <TagInput
          values={wordsToIgnore}
          onChange={setWordsToIgnore}
          placeholder={t("urlRules.create.wordsToIgnorePlaceholder")}
        />
      </div>

      <div>
        <label className="text-xs text-white/50 mb-1 block">{t("urlRules.create.applyTo")}</label>
        <div className="flex gap-1">
          {(["tracked_url", "previous_url", "both"] as const).map((v) => (
            <button
              key={v}
              onClick={() => setApplyTo(v)}
              className={`px-3 py-1.5 text-xs rounded border transition-colors ${applyTo === v ? "bg-emerald-600/20 border-emerald-500/40 text-emerald-300" : "bg-white/[0.04] border-white/[0.08] text-white/60"}`}
            >
              {t(APPLY_TO_LABEL_KEYS[v])}
            </button>
          ))}
        </div>
      </div>

      {/* Advanced settings */}
      <button
        onClick={() => setShowAdvanced(!showAdvanced)}
        className="flex items-center gap-1.5 text-xs text-white/50 hover:text-white/70 transition-colors"
      >
        <Settings2 className="w-3 h-3" />
        {t("urlRules.create.advanced")}
        {showAdvanced ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
      </button>

      {showAdvanced && (
        <div className="space-y-3 p-3 bg-white/[0.02] border border-white/[0.06] rounded-lg">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={baseDomainOnly}
              onChange={() => setBaseDomainOnly(!baseDomainOnly)}
              className="accent-red-500"
            />
            <span className="text-xs text-white/70">{t("urlRules.create.baseDomainOnly")}</span>
          </label>

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={disregardSource}
              onChange={() => setDisregardSource(!disregardSource)}
              className="accent-red-500"
            />
            <span className="text-xs text-white/70">{t("urlRules.create.disregardSource")}</span>
          </label>

          <div>
            <label className="text-xs text-white/50 mb-1 block">
              {t("urlRules.create.unqualified")}
            </label>
            <TagInput
              values={unqualifiedKeywords}
              onChange={setUnqualifiedKeywords}
              placeholder={t("urlRules.create.unqualifiedPlaceholder")}
            />
          </div>

          <div>
            <label className="text-xs text-white/50 mb-1 block">
              {t("urlRules.create.dynamicSource")}
            </label>
            <input
              type="text"
              value={dynamicSourceParam}
              onChange={(e) => setDynamicSourceParam(e.target.value)}
              placeholder="utm_source"
              className="w-full h-8 px-2 text-xs bg-[#030407] border border-white/[0.1] rounded-md text-white placeholder:text-white/20"
            />
          </div>
        </div>
      )}

      <div className="flex gap-2 pt-1">
        <Button
          size="sm"
          className="bg-red-600 hover:bg-red-700 text-white text-xs flex-1"
          disabled={createMutation.isPending || !name || !tag || wordsToMatch.length === 0}
          onClick={() =>
            createMutation.mutate({
              name,
              ruleType,
              tag,
              wordsToMatch,
              wordsToIgnore,
              applyTo,
              baseDomainOnly,
              disregardSource,
              unqualifiedKeywords,
              dynamicSourceParam: dynamicSourceParam || null,
            })
          }
        >
          {createMutation.isPending ? (
            <Loader2 className="h-3 w-3 animate-spin mr-1" />
          ) : (
            <Plus className="h-3 w-3 mr-1" />
          )}
          {t("urlRules.create.add")}
        </Button>
        <Button size="sm" variant="ghost" className="text-xs text-white/50" onClick={onCancel}>
          {t("urlRules.create.cancel")}
        </Button>
      </div>
      {createMutation.isError && (
        <p className="text-xs text-amber-400/70">{t("urlRules.create.tryAgain")}</p>
      )}
    </div>
  );
}

// ─── Main Component ────────────────────────

export default function TrackingUrlRulesPanel() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const { data, isLoading } = useQuery<{ rules: UrlRule[] }>({
    queryKey: ["tracking-url-rules"],
    queryFn: () => apiRequest("GET", "/api/tracking/url-rules"),
    staleTime: 30_000,
  });

  const toggleMutation = useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) =>
      apiRequest("PUT", `/api/tracking/url-rules/${id}`, { enabled }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["tracking-url-rules"] }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiRequest("DELETE", `/api/tracking/url-rules/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["tracking-url-rules"] }),
  });

  const rules = data?.rules || [];
  const filteredRules = searchQuery
    ? rules.filter(
        (r) =>
          r.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          r.tag.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : rules;

  if (isLoading) {
    return (
      <div className="p-3 flex items-center gap-2">
        <Loader2 className="h-4 w-4 animate-spin text-red-400" />
        <span className="text-sm text-white/60">{t("urlRules.loading")}</span>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-xs font-medium text-white/70 flex items-center gap-1.5">
          <Link2 className="w-3.5 h-3.5" /> {t("urlRules.title")}
        </h3>
        {!showCreate && (
          <Button
            size="sm"
            variant="ghost"
            className="text-xs text-red-400 hover:text-red-300 h-6 px-2"
            onClick={() => setShowCreate(true)}
          >
            <Plus className="w-3 h-3 mr-1" /> {t("urlRules.addRule")}
          </Button>
        )}
      </div>

      {/* Info */}
      <div className="p-2.5 bg-blue-500/10 border border-blue-500/20 rounded-lg">
        <p className="text-xs text-blue-300 leading-relaxed">
          <strong>{t("urlRules.howItWorks.label")}</strong> {t("urlRules.howItWorks.text")}
        </p>
      </div>

      {/* Search */}
      {rules.length > 3 && (
        <div className="relative">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-white/30" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t("urlRules.searchPlaceholder")}
            className="w-full h-7 pl-7 pr-2 text-xs bg-[#030407] border border-white/[0.1] rounded-md text-white placeholder:text-white/20"
          />
        </div>
      )}

      {/* Rules table */}
      {filteredRules.length > 0 && (
        <div className="border border-white/[0.08] rounded-lg overflow-hidden">
          {/* Table header */}
          <div className="grid grid-cols-[1fr_100px_100px_180px_60px] gap-0 bg-white/[0.04] px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-white/40 border-b border-white/[0.08]">
            <span>{t("urlRules.table.name")}</span>
            <span>{t("urlRules.table.type")}</span>
            <span>{t("urlRules.table.tag")}</span>
            <span>{t("urlRules.table.match")}</span>
            <span className="text-right">{t("urlRules.table.actions")}</span>
          </div>

          {/* Rules */}
          {filteredRules.map((rule) => (
            <div
              key={rule.id}
              className={`grid grid-cols-[1fr_100px_100px_180px_60px] gap-0 px-3 py-2 border-b border-white/[0.04] items-center transition-colors ${
                rule.enabled ? "hover:bg-white/[0.03]" : "opacity-50"
              }`}
            >
              <span className="text-xs text-white/80 truncate">{rule.name}</span>
              <span className="text-xs text-white/50 uppercase">{rule.ruleType}</span>
              <RuleTagBadge tag={rule.tag} />
              <div className="flex flex-wrap gap-1 max-h-[40px] overflow-hidden">
                {rule.wordsToMatch.slice(0, 3).map((w) => (
                  <span
                    key={w}
                    className="text-[10px] bg-white/[0.06] px-1.5 py-0.5 rounded text-white/60 truncate max-w-[80px]"
                  >
                    {w}
                  </span>
                ))}
                {rule.wordsToMatch.length > 3 && (
                  <span className="text-[10px] text-white/30">+{rule.wordsToMatch.length - 3}</span>
                )}
              </div>
              <div className="flex items-center gap-1 justify-end">
                <button
                  onClick={() => toggleMutation.mutate({ id: rule.id, enabled: !rule.enabled })}
                  className={`p-1 rounded hover:bg-white/[0.06] transition-colors ${rule.enabled ? "text-green-400" : "text-white/30"}`}
                  title={rule.enabled ? t("urlRules.disable") : t("urlRules.enable")}
                >
                  <Power className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={() => {
                    if (confirm(t("urlRules.confirmDelete", { name: rule.name })))
                      deleteMutation.mutate(rule.id);
                  }}
                  className="p-1 rounded hover:bg-red-500/20 text-white/30 hover:text-red-400 transition-colors"
                  title={t("urlRules.delete")}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create form */}
      {showCreate && (
        <CreateUrlRuleForm
          onCreated={() => {
            setShowCreate(false);
            queryClient.invalidateQueries({ queryKey: ["tracking-url-rules"] });
          }}
          onCancel={() => setShowCreate(false)}
        />
      )}

      {/* Empty state */}
      {rules.length === 0 && !showCreate && (
        <div className="p-2.5 bg-white/[0.02] border border-dashed border-white/[0.1] rounded-lg">
          <p className="text-xs text-white/40 mb-2">{t("urlRules.examples")}</p>
          <div className="space-y-1 text-xs text-white/50">
            <div className="flex items-center gap-2">
              <RuleTagBadge tag="$setcall" />
              <span>calendly, booking</span>
            </div>
            <div className="flex items-center gap-2">
              <RuleTagBadge tag="@google_organic" />
              <span>utm_source=google, utm_source=s_google_organic</span>
            </div>
            <div className="flex items-center gap-2">
              <RuleTagBadge tag="@facebook_organic" />
              <span>utm_source=facebook, utm_source=s_facebook_organic</span>
            </div>
            <div className="flex items-center gap-2">
              <RuleTagBadge tag="$calls" />
              <span>phone=, sms_number, Phone1=</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
